/* eslint-disable max-lines */
import { toJS } from 'mobx';

import { getErrorString, Logger, NOOP_LOGGER, SystemEvent } from 'bernie-logger';
import { merge, SerializedData } from 'bernie-core';

import { SOURCE_EVENTS } from './events/source-events';

import {
  AddPropertyToCart,
  Cart,
  CreateMultiItemSession,
  CreateSessionCart,
  EmptyCart,
  InitiateMultiItemSession,
  LodgingPwaPropertySearch,
  OperationResult,
  PrepareCheckout,
  PrepareCheckoutByCartId,
  PropertyInfoPageLayout,
  RemoveItemFromCart,
  Sdk,
  SearchResultsPageLayout,
  SelectProducts,
} from '__generated__/api';

const { withoutKeys } = merge;

export class BexApiSource {
  private readonly api: Sdk;
  private readonly logger: Logger;

  constructor(api: Sdk, logger: Logger = NOOP_LOGGER) {
    this.api = api;
    this.logger = logger;
  }

  public fetchPageLayoutTemplate(variables: PropertyInfoPageLayout.Variables) {
    return this.api
      .PropertyInfoPageLayout(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_LODGING_PROPERTY_INFO_PAGE_LAYOUT_FETCH_ERROR));
  }

  public fetchSRPLayoutTemplate(variables: SearchResultsPageLayout.Variables) {
    return this.api
      .SearchResultsPageLayout(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_LODGING_SEARCH_RESULTS_PAGE_LAYOUT_FETCH_ERROR));
  }

  public searchProperties(
    variables: LodgingPwaPropertySearch.Variables,
    pageId: string,
    isEnableAPQExperiment: boolean
  ): Promise<OperationResult<LodgingPwaPropertySearch.query>> {
    return this.api
      .LodgingPwaPropertySearch(variables, { 'x-page-id': pageId, 'x-enable-apq': String(isEnableAPQExperiment) })
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_LODGING_FULL_PROPERTY_SEARCH_FETCH_ERROR));
  }

  public createSessionCart(variables: CreateSessionCart.Variables): Promise<OperationResult<CreateSessionCart.mutation>> {
    return this.api.CreateSessionCart(variables).catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_CREATE_CART_ERROR));
  }

  public addPropertyToCart(variables: AddPropertyToCart.Variables): Promise<OperationResult<AddPropertyToCart.mutation>> {
    return this.api
      .AddPropertyToCart(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_ADD_ITEM_TO_CART_ERROR));
  }

  public createMultiItemSession(variables: CreateMultiItemSession.Variables): Promise<OperationResult<CreateMultiItemSession.mutation>> {
    return this.api
      .CreateMultiItemSession(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_CREATE_MULTIITEM_SESSION));
  }

  public selectProducts(variables: SelectProducts.Variables): Promise<OperationResult<SelectProducts.mutation>> {
    return this.api.SelectProducts(variables).catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_SELECT_PRODUCTS));
  }

  public initiateMultiItemSession(
    variables: InitiateMultiItemSession.Variables
  ): Promise<OperationResult<InitiateMultiItemSession.mutation>> {
    return this.api
      .InitiateMultiItemSession(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_INITIATE_MULTIITEM_SESSION));
  }

  public fetchCart(variables: Cart.Variables): Promise<OperationResult<Cart.query>> {
    return this.api.Cart(variables).catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_CART_FETCH_ERROR));
  }

  public removeItemFromCart(variables: RemoveItemFromCart.Variables): Promise<OperationResult<RemoveItemFromCart.mutation>> {
    return this.api
      .RemoveItemFromCart(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_REMOVE_ITEM_FROM_CART_ERROR));
  }

  public emptyCart(variables: EmptyCart.Variables): Promise<OperationResult<EmptyCart.mutation>> {
    return this.api
      .EmptyCart(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_REMOVE_ITEM_FROM_CART_ERROR));
  }

  public prepareCheckout(variables: PrepareCheckout.Variables): Promise<OperationResult<PrepareCheckout.mutation>> {
    return this.api
      .PrepareCheckout(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_PREPARE_CHECKOUT_ERROR));
  }

  public prepareCheckoutByCartId(variables: PrepareCheckoutByCartId.Variables): Promise<OperationResult<PrepareCheckoutByCartId.mutation>> {
    return this.api
      .PrepareCheckoutByCartId(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_PREPARE_CHECKOUT_BY_CART_ID_ERROR));
  }

  private getErrorHandler(variables: any, sourceEvent: SystemEvent) {
    return (error: any) => {
      this.logger.logEvent(sourceEvent, variables, getErrorString(error));
      // Throw the error to the next catch block since it is not resolved here
      throw error;
    };
  }

  /**
   * Override the default `toJson` to exclude the Apollo client.
   * If we don't include this, we will get a JSON serialization error.
   *
   * Creates a basic JSON object from the store, excluding
   * any fields provided in `ignoreAdditionalKeys`.
   */
  public toJSON(ignoreAdditionalKeys: string[] | string = []): SerializedData {
    if (!Array.isArray(ignoreAdditionalKeys)) {
      ignoreAdditionalKeys = ignoreAdditionalKeys.constructor === String ? [ignoreAdditionalKeys] : [];
    }

    const regexIgnorePrivate = /^_.+/;
    const simpleObject: any = toJS(this);
    const ignoreKeys: string[] = Object.keys(simpleObject)
      .filter(key => regexIgnorePrivate.test(key) || typeof simpleObject[key] === 'function')
      .concat(ignoreAdditionalKeys, 'logger', 'api', 'headers', 'apiWithFederation');

    return withoutKeys(ignoreKeys).merge({}, simpleObject);
  }
}
